import React, { useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { remittanceActions } from "../../state/remittanceSlice";
import { DownIcon, Equal, MoneyReceive, RightFilledBG } from "../../../../assets/Icons";
import { formatNumber, lastTwoDecimal } from "../../utils";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { ExchangeRateBlack } from "../../images/svg";
import SummaryRow from "../../components/ui/SummaryRow";
import SubSummaryRow from "../../components/ui/SubSummaryRow";
import { AppStrings } from "../../utils";
import { styles } from "./styles";

const CalculationSection: React.FC = () => {
  const dispatch = useAppDispatch();

  const { calculator, remitter, recipient } = useAppSelector((state) => state.remittance);

  // ===== Computed Values =====
  const taxesAndFees =
    calculator?.getTransactionSummary?.data?.total_tax +
    calculator?.getTransactionSummary?.data?.nostro_charges +
    calculator?.getTransactionSummary?.data?.nostro_charges_new +
    calculator?.getTransactionSummary?.data?.hop_fees +
    calculator?.getTransactionSummary?.data?.tcs;

  const vendorFee = useMemo(() => {
    return calculator?.amountCalculation?.roundOff
      ? calculator?.getTransactionSummary?.data?.totalPayInCeils?.find(
          (item: any) => item.id === 10
        )?.hop_fee
      : calculator?.getTransactionSummary?.data?.hop_fees;
  }, [calculator?.amountCalculation?.roundOff, calculator?.getTransactionSummary?.data]);

  const totalYouPay = useMemo(() => {
    return calculator?.amountCalculation?.roundOff
      ? calculator?.getTransactionSummary?.data?.totalPayInCeils?.find(
          (item: any) => item.id === 10
        )?.total_pay
      : calculator?.getTransactionSummary?.data?.total_pay;
  }, [calculator?.amountCalculation?.roundOff, calculator?.getTransactionSummary?.data]);

  return (
    <div className={styles.calculationSection.wrapper}>
      <div className={styles.calculationSection.container}>
        <div className="flex flex-col mt-2 w-full">
          <div className="flex justify-between rounded-lg py-4">
            <div id="left" className="flex flex-row items-center">
              <span className="text-[20px] font-bold text-[#0F5BF1]">
                {remitter?.selectedRemitter?.firstName} {remitter?.selectedRemitter?.lastName}
              </span>
            </div>
            <div className="flex justify-center items-center w-8 h-8 -mt-1">
              <RightFilledBG />
            </div>
            <div className="flex flex-row items-center">
              <span className="text-[20px] font-bold text-[#0F5BF1]">
                {recipient?.selectedRecipient?.firstName || recipient?.selectedRecipient?.lastName}
              </span>
            </div>
          </div>
        </div>

        {/* YOU SEND */}
        <SummaryRow
          icon={
            <div className="rotate-180">
              <DownIcon fillColor="rotate-90" />
            </div>
          }
          title={AppStrings.CALCULATION_SECTION.YOU_SEND}
          amount={formatNumber(calculator?.getTransactionSummary?.data?.user_send)}
          currency="INR"
          loading={
            calculator?.getTransactionSummary?.loading ||
            calculator?.fxRates?.loading ||
            calculator?.cardRate?.loading
          }
        />

        {/* EXCHANGE RATE */}
        <SummaryRow
          icon={
            <div className="bg-neutral-900 w-8 h-8 rounded-full flex items-center justify-center shadow-sm">
              <ExchangeRateBlack />
            </div>
          }
          title={AppStrings.CALCULATION_SECTION.EXCHANGE_RATE}
          amount={formatNumber(calculator?.getTransactionSummary?.data?.conversion_rate)}
          currency="INR"
          loading={
            calculator?.getTransactionSummary?.loading ||
            calculator?.fxRates?.loading ||
            calculator?.cardRate?.loading
          }
        />

        {/* TAXES & FEES */}
        <SummaryRow
          icon={<DownIcon />}
          title={AppStrings.CALCULATION_SECTION.TAXES_AND_FEES}
          amount={formatNumber(taxesAndFees?.toFixed(2))}
          currency="INR"
          loading={
            calculator?.getTransactionSummary?.loading ||
            calculator?.fxRates?.loading ||
            calculator?.cardRate?.loading
          }
        />

        {/* SUB SUMMARY ROWS */}
        <div className={styles.calculationSection.subSectionWrapper}>
          <SubSummaryRow
            title={AppStrings.CALCULATION_SECTION.GST}
            amount={formatNumber(calculator?.getTransactionSummary?.data?.total_tax)}
            currency="INR"
            loading={
              calculator?.getTransactionSummary?.loading ||
              calculator?.fxRates?.loading ||
              calculator?.cardRate?.loading
            }
          />

          <SubSummaryRow
            title={
              calculator?.amountCalculation?.purposeCode === "S0305"
                ? AppStrings.CALCULATION_SECTION.BANK_AND_CONVENIENCE_FEE
                : AppStrings.CALCULATION_SECTION.BANK_FEE
            }
            amount={formatNumber(calculator?.getTransactionSummary?.data?.nostro_charges)}
            currency="INR"
            loading={
              calculator?.getTransactionSummary?.loading ||
              calculator?.fxRates?.loading ||
              calculator?.cardRate?.loading
            }
          />

          <SubSummaryRow
            title={AppStrings.CALCULATION_SECTION.NOSTRO}
            amount={formatNumber(calculator?.getTransactionSummary?.data?.nostro_charges_new)}
            currency="INR"
            loading={
              calculator?.getTransactionSummary?.loading ||
              calculator?.fxRates?.loading ||
              calculator?.cardRate?.loading
            }
          >
            <div className={styles.calculationSection.nostroToggleNonPointer}>
              {!calculator?.amountCalculation?.isBene && (
                <div className={styles.calculationSection.nostroActive}>Our</div>
              )}

              {calculator?.amountCalculation?.isBene && (
                <div className={styles.calculationSection.nostroActive}>Bene</div>
              )}
            </div>
          </SubSummaryRow>

          <SubSummaryRow
            title={AppStrings.CALCULATION_SECTION.VENDOR_FEE}
            amount={formatNumber(lastTwoDecimal(vendorFee))}
            currency="INR"
            loading={
              calculator?.getTransactionSummary?.loading ||
              calculator?.fxRates?.loading ||
              calculator?.cardRate?.loading
            }
          />

          {/* TCS */}
          <div className={styles.calculationSection.tcsRow}>
            <div className={styles.calculationSection.tcsLeft}>
              <div className="mr-3" />
              <p className="mr-2">{AppStrings.CALCULATION_SECTION.TCS}</p>
            </div>

            {calculator?.getTransactionSummary?.loading ||
            calculator?.fxRates?.loading ||
            calculator?.cardRate?.loading ? (
              <Skeleton width={100} height="100%" />
            ) : (
              <div className={styles.calculationSection.tcsRight}>
                <div className={styles.calculationSection.tcsAmount}>
                  {formatNumber(calculator?.getTransactionSummary?.data?.tcs)}
                </div>
                <div className={styles.calculationSection.currency}>INR</div>
              </div>
            )}
          </div>
        </div>

        {/* YOU PAY */}
        <div className="border border-gray-500 rounded-full my-4" />
        <SummaryRow
          icon={
            <div className="rotate-180">
              <Equal />
            </div>
          }
          title={AppStrings.CALCULATION_SECTION.YOU_PAY}
          amount={formatNumber(totalYouPay)}
          currency="INR"
          loading={
            calculator?.getTransactionSummary?.loading ||
            calculator?.fxRates?.loading ||
            calculator?.cardRate?.loading
          }
        >
          <div className="flex flex-row items-center justify-between ml-3">
            <button
              onClick={() =>
                dispatch(
                  remittanceActions.setAmountCalculation({
                    roundOff: !calculator?.amountCalculation?.roundOff,
                  })
                )
              }
              className={`w-full flex justify-center items-center p-1 rounded-md ${
                calculator?.amountCalculation?.roundOff
                  ? "bg-primary-100 text-white cursor-pointer"
                  : "bg-[#E3E3E3] text-black"
              } ${
                calculator?.amountCalculation?.purposeCode !== "S0305"
                  ? "cursor-not-allowed opacity-15"
                  : ""
              }`}
              disabled={calculator?.amountCalculation?.purposeCode !== "S0305" ? true : false}
            >
              Round Off
            </button>
          </div>
        </SummaryRow>

        {/* THEY RECEIVE */}
        <div className="-mt-3"></div>
        <SummaryRow
          icon={
            <div className="">
              <MoneyReceive />
            </div>
          }
          title={AppStrings.CALCULATION_SECTION.THEY_RECEIVE}
          amount={formatNumber(calculator?.getTransactionSummary?.data?.reciepient_recieve)}
          currency={calculator?.getTransactionSummary?.data?.dest_currency}
          loading={
            calculator?.getTransactionSummary?.loading ||
            calculator?.fxRates?.loading ||
            calculator?.cardRate?.loading
          }
        />
      </div>
    </div>
  );
};

export default CalculationSection;
